import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { usePollWhileOffline } from '../lib/live';
import { advance, isActive, Lane, laneOf, LATE_MINUTES, minutesSince } from '../lib/kitchen';
import { main } from '../lib/text';
import type { Order } from '../lib/types';
import { Shell } from './Shell';
import { handleAuthError, useStaff } from './useStaff';

const LANES: { id: Lane; title: string; tint: string; btn: string }[] = [
  { id: 'new', title: 'ใหม่ · New', tint: '#BBD3FF', btn: 'เริ่มทำ · Start cooking' },
  { id: 'cooking', title: 'กำลังทำ · Cooking', tint: '#FFE2A6', btn: 'พร้อมเสิร์ฟ · Ready to serve' },
  { id: 'ready', title: 'พร้อมเสิร์ฟ · Ready', tint: '#BFEBD0', btn: 'เสิร์ฟแล้ว · Served' },
];

/** /kitchen: tickets in three lanes, oldest first; one tap moves a ticket on. Live over WebSocket. */
export function Kitchen({ path }: { path: string }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { s, online } = useStaff((e) => {
    if (e.kind !== 'order') return;
    const o = e.order as Order;
    setOrders((prev) => {
      const rest = prev.filter((x) => x.id !== o.id);
      return isActive(o) ? [...rest, o].sort((a, b) => a.placedAt.localeCompare(b.placedAt) || a.number - b.number) : rest;
    });
  }, () => void load());

  const load = useCallback(async () => {
    if (!s) return;
    try { setOrders((await api<{ orders: Order[] }>('GET', '/kitchen', { token: s.token })).orders); }
    catch (e) { handleAuthError(e); setErr((e as Error).message); }
  }, [s]);
  useEffect(() => { void load(); }, [load]);
  usePollWhileOffline(online, () => void load());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);

  const move = async (o: Order) => {
    const step = advance(o);
    if (!s || !step) return;
    setBusy(o.id);
    setErr(null);
    try {
      await api('PUT', '/kitchen/lines', { token: s.token, body: { status: step.status, lines: step.lineIds.map((lineId) => ({ sessionId: o.sessionId, orderId: o.id, lineId })) } });
    } catch (e) {
      handleAuthError(e);
      setErr((e as Error).message);
      void load();
    } finally {
      setBusy(null);
    }
  };

  const late = orders.filter((o) => laneOf(o) !== 'ready' && minutesSince(o.placedAt, now) >= LATE_MINUTES).length;
  return (
    <Shell path={path} online={online} sub={late ? `${late} late` : undefined}>
      {err && <div className="warn" role="alert" style={{ color: 'var(--ink)', marginBottom: 12 }}>{err}</div>}
      <div className="lanes">
        {LANES.map((lane) => {
          const tickets = orders.filter((o) => laneOf(o) === lane.id);
          return (
            <section key={lane.id} className="panel" aria-label={lane.title}>
              <div className="lane-head">
                <h2 className="display" style={{ margin: 0, fontWeight: 500, fontSize: 18 }}>{lane.title}</h2>
                <span className="count" style={{ background: lane.tint }}>{tickets.length}</span>
              </div>
              {tickets.length === 0 && <div className="empty-lane">—</div>}
              {tickets.map((o) => {
                const mins = minutesSince(o.placedAt, now);
                const isLate = lane.id !== 'ready' && mins >= LATE_MINUTES;
                const pct = Math.min(100, Math.round((mins / LATE_MINUTES) * 100));
                const notes = o.lines.filter((l) => l.note && l.status !== 'cancelled');
                return (
                  <article key={o.id} className={`kt${isLate ? ' late' : ''}`}>
                    <div className="row">
                      <span className="display" style={{ fontSize: 20 }}>โต๊ะ {o.tableName} <span className="muted" style={{ fontSize: 14 }}>#{o.number}</span></span>
                      <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: isLate ? '#C42A30' : undefined }}>{mins} min</span>
                    </div>
                    <div className="bar"><div style={{ width: `${pct}%`, background: isLate ? 'var(--red)' : mins >= 10 ? 'var(--mango-dark)' : '#2E9E5B' }} /></div>
                    <ul>
                      {o.lines.filter((l) => l.status !== 'cancelled').map((l) => (
                        <li key={l.id} className={l.status === 'served' || (lane.id !== 'ready' && l.status === 'ready') ? 'done' : ''}>
                          <span className="q">{l.qty}×</span>
                          <span>{main(l.name)}{l.choices.length > 0 && <span className="opts"> · {l.choices.map((c) => main(c.name)).join(', ')}</span>}</span>
                        </li>
                      ))}
                    </ul>
                    {notes.map((l) => <span key={l.id} className="knote">{main(l.name)}: {l.note}</span>)}
                    <button className={`kbtn ${lane.id}`} disabled={busy === o.id} onClick={() => void move(o)}>{lane.btn}</button>
                  </article>
                );
              })}
            </section>
          );
        })}
      </div>
    </Shell>
  );
}
